// src/pages/SyncPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify'; // Menggunakan react-toastify
import './SyncPage.css';

function SyncPage() {
  const [settings, setSettings] = useState({ interval_days: 1, run_hour: 3, is_enabled: false });
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [logContent, setLogContent] = useState("Loading logs...");
  const [realtimeLogs, setRealtimeLogs] = useState("Loading real-time logs...");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [logLines, setLogLines] = useState(100);
  const { authFetch } = useAuth(); // Hanya butuh authFetch

  // Fungsi untuk memuat data awal (pengaturan dan log)
  const fetchData = async () => {
    try {
      const settingsResponse = await authFetch('/api/scheduler/settings');
      const settingsData = await settingsResponse.json();
      if (settingsData && !settingsData.error) {
        setSettings(settingsData);
      }
    } catch (error) {
      console.error("Gagal mengambil pengaturan:", error);
    }

    try {
      const logsResponse = await authFetch('/api/sync-logs');
      const logsData = await logsResponse.json();
      setLogContent(logsData.log_content || logsData.error || "Gagal memuat log.");
    } catch (error) {
      console.error("Gagal mengambil log:", error);
      setLogContent("Gagal memuat log.");
    }
  };

  // Fungsi untuk memuat real-time logs
  const fetchRealtimeLogs = async () => {
    try {
      const response = await authFetch(`/api/realtime-logs?lines=${logLines}`);
      const data = await response.json();
      
      if (data.success) {
        setRealtimeLogs(data.logs || "No logs available.");
      } else {
        setRealtimeLogs(`Error: ${data.error}`);
      }
    } catch (error) {
      console.error("Gagal mengambil real-time logs:", error);
      setRealtimeLogs(`Failed to fetch logs: ${error.message}`);
    }
  };

  // useEffect berjalan HANYA SEKALI saat komponen dimuat
  useEffect(() => {
    fetchData();
    fetchRealtimeLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh real-time logs
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchRealtimeLogs();
      }, 3000); // Refresh every 3 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, logLines]);

  // State untuk filter tanggal
  const today = new Date();
  const nineDaysAgo = new Date();
  nineDaysAgo.setDate(today.getDate() - 9);
  
  const [dateRange, setDateRange] = useState({
    start: nineDaysAgo.toISOString().slice(0, 10),
    end: today.toISOString().slice(0, 10)
  });

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: value }));
  };

  const handleSettingChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue;
    if (type === 'checkbox') {
      newValue = checked;
    } else if (name === 'sync_interval') {
      newValue = value;
    } else {
      newValue = parseInt(value, 10);
    }
    
    setSettings(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      const response = await authFetch('/api/scheduler/settings', {
        method: 'POST',
        body: JSON.stringify(settings)
      });
      if (!response.ok) throw new Error('Gagal menyimpan pengaturan');
      toast.success('Pengaturan berhasil disimpan!');
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleManualSync = async () => {
    if (!window.confirm('Jalankan sinkronisasi manual sekarang?')) return;
    setIsSyncing(true);
    setLogContent("Sinkronisasi sedang berjalan...");
    try {
      const response = await authFetch('/api/trigger-sync', { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          start_date: dateRange.start, 
          end_date: dateRange.end 
        })
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Sync failed');
      toast.success('Sinkronisasi manual selesai!');
      
      fetchData(); // Muat ulang log setelah sinkronisasi

    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setLogContent(`Sinkronisasi gagal: ${error.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="sync-page">
      <h1>Sync Data</h1>
      
      <div className="sync-section">
        <h2>Manual Sync</h2>
        <p>Jalankan proses sinkronisasi data secara manual kapan pun diperlukan.</p>
        
        <div className="manual-sync-filters">
          <label>Date Range:</label>
          <input type="date" name="start" value={dateRange.start} onChange={handleDateChange} />
          <span>-</span>
          <input type="date" name="end" value={dateRange.end} onChange={handleDateChange} />
        </div>

        <button onClick={handleManualSync} disabled={isSyncing} className="sync-button">
          {isSyncing ? 'Syncing...' : 'Run Sync Now'}
        </button>
      </div>

      <div className="sync-section">
        <h2>Scheduled Sync</h2>
        <p>Atur sinkronisasi otomatis untuk berjalan secara berkala.</p>
        <div className="settings-form">
          <div className="form-group-toggle">
            <label>Enable Scheduled Sync</label>
            <label className="switch">
              <input type="checkbox" name="is_enabled" checked={settings.is_enabled || false} onChange={handleSettingChange} />
              <span className="slider round"></span>
            </label>
          </div>
          <div className="form-group">
            <label>Terakhir Sync</label>
            <span>{settings.last_sync_at ? new Date(settings.last_sync_at).toLocaleString() : 'Belum pernah'}</span>
          </div>
          <div className="form-group">
            <label>Interval Sync</label>
            <select name="sync_interval" value={settings.sync_interval || 'daily'} onChange={handleSettingChange}>
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div className="form-group">
            <label>Run every</label>
            <input type="number" name="interval_days" value={settings.interval_days || 1} onChange={handleSettingChange} min="1" />
            <label>day(s)</label>
          </div>
          <div className="form-group">
            <label>At hour</label>
            <input type="number" name="run_hour" value={settings.run_hour || 0} onChange={handleSettingChange} min="0" max="23" />
            <label>(0-23 UTC)</label>
          </div>
          <button onClick={handleSaveSettings} disabled={isSaving} className="save-button">
            {isSaving ? 'Saving...' : 'Save Schedule'}
          </button>
        </div>
      </div>

      <div className="sync-section">
        <h2>Real-Time System Logs</h2>
        <p>Live logs from journalctl (proyekflask service)</p>
        
        <div className="log-controls">
          <div className="form-group">
            <label>Lines to show:</label>
            <select value={logLines} onChange={(e) => setLogLines(parseInt(e.target.value))}>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="500">500</option>
            </select>
          </div>
          
          <div className="form-group-toggle">
            <label>Auto-refresh (3s)</label>
            <label className="switch">
              <input 
                type="checkbox" 
                checked={autoRefresh} 
                onChange={(e) => setAutoRefresh(e.target.checked)} 
              />
              <span className="slider round"></span>
            </label>
          </div>
          
          <button onClick={fetchRealtimeLogs} className="refresh-button">
            🔄 Refresh Now
          </button>
        </div>
        
        <div className="log-container realtime-logs">
          <pre>{realtimeLogs}</pre>
        </div>
      </div>

      <div className="sync-section">
        <h2>Sync Failure Log</h2>
        <div className="log-container">
          <pre>{logContent}</pre>
        </div>
      </div>
    </div>
  );
}

export default SyncPage;
